/**
 * Compare-mode cluster of a metric condition row: the controls, the live
 * plain-English sentence, and — when the drawer's live preview has data — a
 * REAL example computed from an actual item on this rule's layer, plus the
 * hover "how it's calculated" text with those same numbers. Rendered as a
 * fragment inside the row's flex-wrap: the help line is `order-last
 * basis-full`, so it wraps under the whole row wherever it sits in the DOM.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useMemo } from 'react';
import type { FlowLayer, RuleNode } from '@trafficpulse/shared';

import CompareHelpLine from '../../../../compare/CompareHelpLine';
import { compareCalcTitle } from '../../../../compare/compare-example';
import { compareHelp } from '../../../../compare/compare-sentence';
import { pickCompareSample } from '../../../../compare/live/sample';
import { useOptionalFlowOptimizerLivePreview } from '../../../../context/FlowOptimizerLivePreviewContext';
import CompareToControls, {
  compareWindowLabelOf,
  type CompareToControlsProps,
} from '../CompareToControls';
import { metricLabel } from '../metric-options';

export interface CompareSectionProps {
  node: Extract<RuleNode, { kind: 'condition' }>;
  metrics: CompareToControlsProps['metrics'];
  layer: FlowLayer;
  /** Cube key the group's conditions read (the inherit target). */
  windowKey: string | null;
  onChange: (next: RuleNode) => void;
}

const CompareSection: FC<CompareSectionProps> = ({
  node,
  metrics,
  layer,
  windowKey,
  onChange,
}) => {
  const ct = node.compareTo;
  const items = useOptionalFlowOptimizerLivePreview()?.result?.items;
  const sample = useMemo(
    () =>
      ct
        ? pickCompareSample({
            items,
            layer,
            metric: node.metric,
            compareTo: ct,
            windowKey,
          })
        : null,
    [items, layer, node.metric, ct, windowKey],
  );
  if (!ct) return null;

  const lhsLabel = metricLabel(metrics, node.metric);
  const rhsLabel =
    (ct.against === 'total' ? 'TOTAL ' : '') + metricLabel(metrics, ct.metric);
  const windowLabel = compareWindowLabelOf(ct);
  const help = compareHelp({
    lhsLabel,
    op: node.op,
    percent: ct.percent,
    rhsLabel,
    windowLabel,
    inheritLabel: 'the same window',
    sample,
  });
  // A plain direct comparison reads fine off the controls alone — the helper
  // earns its line only when a % is active (the confusing case) or when it
  // has a warning to raise.
  const showHelp = ct.percent !== undefined || help.kind !== 'sentence';
  return (
    <>
      <CompareToControls
        node={node}
        metrics={metrics}
        calcTitle={compareCalcTitle({
          lhsLabel,
          op: node.op,
          percent: ct.percent,
          rhsLabel,
          window: windowLabel ?? 'the same window',
          sample,
        })}
        addTitle={`Add a % — e.g. 80 turns "${lhsLabel} ${node.op} ${rhsLabel}" into "${lhsLabel} ${node.op} 80% of ${rhsLabel}".`}
        onChange={onChange}
      />
      {showHelp && <CompareHelpLine className="order-last basis-full" help={help} />}
    </>
  );
};

export default CompareSection;
